"use client";

import { useEffect, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import {
  parseRideEstimate,
  type RideEstimate,
  type RidePoint,
} from "@/lib/homepageRideModes";

type Props = {
  pickup: RidePoint | null;
  destination: RidePoint | null;
  map: LeafletMap | null;
};

export default function RideRoutePreview({
  pickup,
  destination,
  map,
}: Props) {
  const [state, setState] = useState<{
    estimate: RideEstimate | null;
    loading: boolean;
    error: string;
  }>({
    estimate: null,
    loading: false,
    error: "",
  });

  const pickupLat = pickup?.latitude;
  const pickupLng = pickup?.longitude;
  const destinationLat = destination?.latitude;
  const destinationLng = destination?.longitude;

  useEffect(() => {
    if (
      pickupLat === undefined ||
      pickupLng === undefined ||
      destinationLat === undefined ||
      destinationLng === undefined
    ) {
      return;
    }

    const controller = new AbortController();

    setState({ estimate: null, loading: true, error: "" });

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          fromLatitude: String(pickupLat),
          fromLongitude: String(pickupLng),
          toLatitude: String(destinationLat),
          toLongitude: String(destinationLng),
        });

        const response = await fetch(
          `/api/route-estimate?${params}`,
          { cache: "no-store", signal: controller.signal }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Road route unavailable.");
        }

        const estimate = parseRideEstimate(data);

        if (!controller.signal.aborted) {
          setState({ estimate, loading: false, error: "" });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setState({
            estimate: null,
            loading: false,
            error: error instanceof Error
              ? error.message
              : "Could not calculate road route.",
          });
        }
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pickupLat, pickupLng, destinationLat, destinationLng]);

  useEffect(() => {
    if (!map || !state.estimate) return;

    let disposed = false;
    let routeLayer: import("leaflet").Polyline | null = null;

    void import("leaflet").then((L) => {
      if (disposed) return;

      routeLayer = L.polyline(state.estimate!.coordinates, {
        color: "#4f46e5",
        weight: 5,
        opacity: 0.85,
        interactive: false,
      }).addTo(map);

      map.fitBounds(routeLayer.getBounds(), {
        padding: [55, 55],
        maxZoom: 14,
      });
    });

    return () => {
      disposed = true;
      routeLayer?.remove();
    };
  }, [map, state.estimate]);

  if (!pickup || !destination) return null;

  return (
    <div className="text-xs font-semibold text-indigo-800">
      {state.loading && <p>Calculating road route...</p>}
      {state.error && <p role="alert">{state.error}</p>}
      {state.estimate && (
        <p>
          Road route: {state.estimate.distanceKm} km ·{" "}
          {state.estimate.durationMinutes} min
        </p>
      )}
    </div>
  );
}
